import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Use Cases
import {
  parsePageNumber,
  updateSearchParams,
  validatePageNumber,
  calculateOptimalPageForSizeChange,
} from './tablePaginationUseCase';

// Default configuration
const DEFAULT_PAGE_NUMBER = '1';

/**
 * @type {Array<TablePaginationOptions>}
 * Mock for table pagination options
 */
export const PAGE_SIZE_OPTIONS = [
  {
    label: '50',
    value: '50',
  },
  {
    label: '100',
    value: '100',
  },
  {
    label: '500',
    value: '500',
  },
  {
    label: 'Tous',
    value: 'none',
  },
];

/**
 * @typedef {object} useTablePaginationReturn
 * @property {Array<TablePaginationOptions>} pageSizeOptions - Page size options
 * @property {number} pageNumber - Current page number
 * @property {Function} setPageNumber - Function to set the page number
 * @property {(size: number) => void} setPageNumberFromPageSize - Updates the URL page param to keep the user's position when page size changes
 * @property {(page: number, totalPages: number) => void} goToPage - Function to go to an arbitrary page (clamped)
 * @property {Function} goToNextPage - Function to go to the next page
 * @property {Function} goToPreviousPage - Function to go to the previous page
 * @property {() => void} resetPageToFirst - Sets URL `page` query param to 1 (e.g. after filter changes)
 */

/**
 * Hook to manage table pagination state using URL search params.
 * The SPA uses the query key `page` in the browser URL (`?page=2`). The GET `/table`
 * request is built in `getTableData` with the API’s query key `pageNumber`; the two
 * names are independent and do not need to match.
 * @param {object} [props] - Configuration options
 * @param {string} [props.initialPageSize] - Initial page size
 * @param {string} [props.defaultPageNumber='1'] - Default page number
 * @param {boolean} [props.isLoading] - Whether the data is loading
 * @returns {useTablePaginationReturn} Pagination state and actions
 */
const useTablePagination = ({
  initialPageSize,
  defaultPageNumber = DEFAULT_PAGE_NUMBER,
  isLoading,
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  /** @type {number} */
  const pageNumber = useMemo(() => {
    const pageParam = searchParams.get('page');
    return parsePageNumber(pageParam, parseInt(defaultPageNumber, 10));
  }, [searchParams, defaultPageNumber]);

  // Update search params helper
  const updateParams = useCallback((
    /** @type {Record<string, string | number | null | undefined>} */ updates,
  ) => {
    setSearchParams((prev) => updateSearchParams(prev, updates));
  }, [setSearchParams]);

  /**
   * Set the page number
   * @param {number} page - The page number
   * @param {number} totalPages - The total number of pages
   */
  const setPageNumber = useCallback((
    /** @type {number} */ page,
    /** @type {number} */ totalPages,
  ) => {
    const validPage = validatePageNumber(page, totalPages);
    updateParams({ page: validPage });
  }, [updateParams]);

  /**
   * Updates the URL page param so the user keeps a consistent position when changing page size.
   * @param {number} size - The new page size
   */
  const setPageNumberFromPageSize = useCallback((
    /** @type {number} */ size,
  ) => {
    if (isLoading) return;

    const initialPageSizeNumber = parseInt(initialPageSize, 10);
    const optimalPage = calculateOptimalPageForSizeChange(pageNumber, initialPageSizeNumber, size);

    updateParams({ page: optimalPage });
  }, [updateParams, pageNumber, initialPageSize, isLoading]);

  /**
   * Go to next page
   * @param {boolean} hasNextPage - Whether there is a next page
   */
  const goToNextPage = useCallback((
    /** @type {boolean} */ hasNextPageArg,
    /** @type {number} */ totalPages,
  ) => {
    if (hasNextPageArg) setPageNumber(pageNumber + 1, totalPages);
  }, [pageNumber, setPageNumber]);

  /**
   * Go to previous page
   * @param {boolean} hasPreviousPage - Whether there is a previous page
   */
  const goToPreviousPage = useCallback((
    /** @type {boolean} */ hasPreviousPageArg,
    /** @type {number} */ totalPages,
  ) => {
    if (hasPreviousPageArg) setPageNumber(pageNumber - 1, totalPages);
  }, [pageNumber, setPageNumber]);

  /**
   * Go to an arbitrary page, clamped between 1 and totalPages.
   * @param {number} page - The target page number
   * @param {number} totalPages - The total number of pages
   */
  const goToPage = useCallback((
    /** @type {number} */ page,
    /** @type {number} */ totalPages,
  ) => {
    setPageNumber(page, totalPages);
  }, [setPageNumber]);

  /** Sets pagination to page 1 in the URL (used when filters change the result set). */
  const resetPageToFirst = useCallback(() => {
    updateParams({ page: 1 });
  }, [updateParams]);

  return {
    // Options
    pageSizeOptions: PAGE_SIZE_OPTIONS,

    // State
    pageNumber,

    // Actions
    setPageNumber,
    setPageNumberFromPageSize,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetPageToFirst,
  };
};

export default useTablePagination;
